/* Per-field diff builder for the proposal review screen (Ale 2026-07-06).
 *
 * An update_* proposal changes one OR MORE fields (units_held +
 * effective_date + units_currency in one document is common), so the
 * review row renders a before→after line for EVERY changed field, not
 * just the primary units pair.
 *
 * The "old" side prefers `current_values` — the target row's LIVE values
 * the backend reads at fetch time — over the `previous_*` keys stamped
 * into proposed_data at build time (those are the pipeline's snapshot
 * and go stale if the record changed between analysis and review).
 * Pure module, no React: imported by the active confirm surface
 * (components/review/EventCard); unit-tested from
 * tests/e2e/approve-diff.spec.ts. Lives in lib/ because it is shared
 * across the v1 and v2 trees (WI-8).
 */

export interface FieldDiff {
  field: string
  old: unknown
  new: unknown
}

type Data = Record<string, unknown>

/** Scalar we can render; objects (e.g. source_coordinate) never diff. */
function renderable(v: unknown): boolean {
  return v == null || typeof v !== 'object'
}

/** Value-level equality: 100, "100" and 100.0 are the same reading. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  const na = Number(a)
  const nb = Number(b)
  if (String(a).trim() !== '' && String(b).trim() !== '' && !Number.isNaN(na) && !Number.isNaN(nb))
    return na === nb
  return String(a) === String(b)
}

/** Field names the builder snapshots the old value under. */
function snapshotKeyFor(field: string, proposed: Data): string | null {
  for (const k of [`previous_${field}`, `existing_${field}`]) {
    if (k in proposed) return k
  }
  return null
}

/** The old-side reading for one field: live record first, snapshot second. */
function oldValueFor(field: string, proposed: Data, current: Data | null | undefined): unknown {
  if (current && field in current) return current[field]
  const snap = snapshotKeyFor(field, proposed)
  return snap ? proposed[snap] : undefined
}

/**
 * Build one {field, old, new} row per CHANGED field of an update proposal.
 *
 * Candidate fields are the union of the keys the backend resolved live
 * (`current_values`) and the fields with a `previous_*`/`existing_*`
 * snapshot in proposed_data — i.e. everything we have an old side for.
 * A row is emitted when the proposal carries a renderable new value that
 * differs from the old reading.
 */
export function buildFieldDiffs(proposed: Data, current: Data | null | undefined): FieldDiff[] {
  const fields: string[] = []
  const seen = new Set<string>()
  const push = (f: string) => {
    if (!seen.has(f)) {
      seen.add(f)
      fields.push(f)
    }
  }
  for (const k of Object.keys(proposed)) {
    const m = /^(?:previous|existing)_(.+)$/.exec(k)
    if (m) push(m[1])
  }
  if (current) for (const k of Object.keys(current)) push(k)

  const diffs: FieldDiff[] = []
  for (const field of fields) {
    const next = proposed[field]
    if (next === undefined || !renderable(next)) continue
    const prev = oldValueFor(field, proposed, current)
    if (prev === undefined && next == null) continue
    if (!sameValue(prev, next)) diffs.push({ field, old: prev ?? null, new: next })
  }
  return diffs
}

/**
 * True when the record moved between analysis and review: the live
 * `current_values` disagree with the `previous_*`/`existing_*` snapshot
 * the builder stamped. Drives the "record changed since analysis" note.
 */
export function recordChangedSinceAnalysis(
  proposed: Data,
  current: Data | null | undefined
): boolean {
  if (!current) return false
  for (const k of Object.keys(proposed)) {
    const m = /^(?:previous|existing)_(.+)$/.exec(k)
    if (!m || !(m[1] in current)) continue
    if (!sameValue(proposed[k], current[m[1]])) return true
  }
  return false
}
